import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendChatMessage, uploadFile, getEmails, getDrafts } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Bot, Send, Loader2, User, Sparkles, Trash2, Paperclip, FileText, X,
  LayoutDashboard, Mail, Users, FileEdit, CheckCircle, Settings,
  FolderOpen, FileImage, Palette, BookOpen, ArrowRight, ChevronDown, ChevronUp,
  MessageSquare, Zap, RefreshCw, ExternalLink, Plus
} from 'lucide-react';

function ChatPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showControlPanel, setShowControlPanel] = useState(true);
  const [stats, setStats] = useState({ emails: 0, drafts: 0, pending: 0 });
  const [loadingStats, setLoadingStats] = useState(true);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load dashboard stats
  useEffect(function() {
    loadStats();
  }, []);

  async function loadStats() {
    setLoadingStats(true);
    try {
      const [emailsRes, draftsRes] = await Promise.all([
        getEmails('new').catch(() => ({ data: [] })),
        getDrafts().catch(() => ({ data: [] }))
      ]);
      setStats({
        emails: emailsRes.data?.length || 0,
        drafts: draftsRes.data?.length || 0,
        pending: 0
      });
    } catch(e) {
      console.error('Stats error:', e);
    } finally {
      setLoadingStats(false);
    }
  }

  // Quick navigation items
  const controlPanelItems = [
    { 
      category: 'البريد والتواصل', 
      categoryEn: 'Email & Communication',
      items: [
        { path: '/emails', icon: Mail, label: 'صندوق الوارد', labelEn: 'Inbox', color: 'bg-blue-500/10 text-blue-500', stat: stats.emails },
        { path: '/drafts', icon: FileEdit, label: 'المسودات', labelEn: 'Drafts', color: 'bg-amber-500/10 text-amber-500', stat: stats.drafts },
      ]
    },
    { 
      category: 'إدارة الأعمال', 
      categoryEn: 'Business Management',
      items: [
        { path: '/suppliers', icon: Users, label: 'الموردين', labelEn: 'Suppliers', color: 'bg-green-500/10 text-green-500' },
        { path: '/quotes', icon: FileText, label: 'عروض الأسعار', labelEn: 'Quotes', color: 'bg-purple-500/10 text-purple-500' },
        { path: '/approvals', icon: CheckCircle, label: 'الموافقات', labelEn: 'Approvals', color: 'bg-cyan-500/10 text-cyan-500' },
        { path: '/submittals', icon: FolderOpen, label: 'Submittals', labelEn: 'Submittals', color: 'bg-indigo-500/10 text-indigo-500' },
      ]
    },
    { 
      category: 'أدوات التصميم', 
      categoryEn: 'Design Tools',
      items: [
        { path: '/design-studio', icon: Palette, label: 'استوديو التصميم', labelEn: 'Design Studio', color: 'bg-pink-500/10 text-pink-500' },
        { path: '/profile-generator', icon: BookOpen, label: 'منشئ البروفايل', labelEn: 'Profile Generator', color: 'bg-orange-500/10 text-orange-500' },
        { path: '/pdf-modifier', icon: FileImage, label: 'تعديل PDF', labelEn: 'PDF Modifier', color: 'bg-red-500/10 text-red-500' },
      ]
    },
    { 
      category: 'النظام', 
      categoryEn: 'System',
      items: [
        { path: '/', icon: LayoutDashboard, label: 'لوحة التحكم', labelEn: 'Dashboard', color: 'bg-slate-500/10 text-slate-500' },
        { path: '/ai-services', icon: Sparkles, label: 'خدمات AI', labelEn: 'AI Services', color: 'bg-violet-500/10 text-violet-500' },
        { path: '/settings', icon: Settings, label: 'الإعدادات', labelEn: 'Settings', color: 'bg-gray-500/10 text-gray-500' },
      ]
    },
  ];

  // Quick actions for Nicole
  const quickActions = [
    { label: 'إرسال بريد جديد', labelEn: 'Send Email', action: () => navigate('/drafts'), icon: Plus },
    { label: 'تصميم صورة', labelEn: 'Design Image', action: () => navigate('/design-studio'), icon: Palette },
    { label: 'إنشاء بروفايل', labelEn: 'Create Profile', action: () => navigate('/profile-generator'), icon: BookOpen },
    { label: 'تعديل PDF', labelEn: 'Edit PDF', action: () => navigate('/pdf-modifier'), icon: FileImage },
  ];

  useEffect(function() {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  async function handleFileSelect(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain', 'image/png', 'image/jpeg'];
    
    // Check total files limit (max 5)
    if (attachedFiles.length + files.length > 5) {
      toast.error('يمكنك إرفاق 5 ملفات كحد أقصى');
      return;
    }

    setUploadingFile(true);
    const newFiles = [];
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`حجم الملف ${file.name} كبير جداً. الحد الأقصى 10MB`);
        continue;
      }

      try {
        const response = await uploadFile(file);
        // Handle different file types - Excel has sheets, PDF/Word has text
        let analysisText = 'File uploaded successfully';
        if (response.data.analysis?.sheets) {
          // Excel file - format sheets content
          const sheets = response.data.analysis.sheets;
          analysisText = Object.entries(sheets).map(([sheetName, rows]) => {
            const headerRow = rows[0] || [];
            const dataRows = rows.slice(1, 51); // Limit to 50 rows
            let content = `📊 Sheet: ${sheetName}\n`;
            content += headerRow.join(' | ') + '\n';
            dataRows.forEach(row => {
              content += row.map(cell => cell !== null ? cell : '').join(' | ') + '\n';
            });
            if (rows.length > 51) {
              content += `... (${rows.length - 51} more rows)\n`;
            }
            return content;
          }).join('\n');
        } else if (response.data.analysis?.text) {
          analysisText = response.data.analysis.text;
        } else if (response.data.analysis?.error) {
          analysisText = response.data.analysis.error;
        }
        const filePath = response.data.attachment?.file_path || response.data.file_path || null;
        
        // DEBUG: Log file path from upload response
        console.log('[ChatPage] File uploaded:', {
          name: file.name,
          file_path: filePath,
          responseKeys: Object.keys(response.data),
          attachment: response.data.attachment
        });
        
        if (!filePath) {
          console.error('[ChatPage] ERROR: No file_path in upload response for', file.name);
        }
        
        newFiles.push({
          id: Date.now() + Math.random(),
          name: file.name,
          type: file.type,
          size: file.size,
          content: analysisText,
          file_path: filePath
        });
        toast.success(`تم رفع ${file.name} بنجاح`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`فشل رفع ${file.name}`);
      }
    }
    
    setAttachedFiles(prev => [...prev, ...newFiles]);
    setUploadingFile(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function removeAttachedFile(fileId) {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  }

  function clearAllFiles() {
    setAttachedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  async function handleSend() {
    if (!input.trim() && attachedFiles.length === 0) return;

    const userMessage = input || 'حلل هذه الملفات / Analyze these files';
    
    // Build display message with all attached files
    let displayMessage = userMessage;
    if (attachedFiles.length > 0) {
      displayMessage += '\n' + attachedFiles.map(f => '📎 ' + f.name).join('\n');
    }
    
    setInput('');
    setMessages(function(prev) { return [...prev, { role: 'user', content: displayMessage, files: attachedFiles }]; });
    setLoading(true);

    try {
      // Combine all file contents
      const combinedContent = attachedFiles.length > 0 
        ? attachedFiles.map(f => `\n=== ملف: ${f.name} ===\n${f.content}`).join('\n\n')
        : null;
      const fileNames = attachedFiles.length > 0 
        ? attachedFiles.map(f => f.name).join(', ')
        : null;
      // Get file paths for attachments
      const filePaths = attachedFiles.length > 0
        ? attachedFiles.filter(f => f.file_path).map(f => f.file_path)
        : [];
      
      // DEBUG: Log file paths being sent
      console.log('[ChatPage] Sending message with attachments:', {
        filesCount: attachedFiles.length,
        filesWithPaths: attachedFiles.filter(f => f.file_path).length,
        filePaths: filePaths,
        allFiles: attachedFiles.map(f => ({ name: f.name, file_path: f.file_path }))
      });
      
      // Show warning if files without paths
      const filesWithoutPaths = attachedFiles.filter(f => !f.file_path);
      if (filesWithoutPaths.length > 0) {
        console.warn('[ChatPage] WARNING: Some files have no file_path:', filesWithoutPaths.map(f => f.name));
        toast.warning(`${filesWithoutPaths.length} ملف(ات) بدون مسار - قد لا يتم إرفاقها بالبريد`);
      }
      
      const response = await sendChatMessage(userMessage, null, sessionId, combinedContent, fileNames, filePaths);
      setSessionId(response.data.session_id);
      
      // Check if there's a generated image
      const generatedImage = response.data.generated_image;
      const generatedPdf = response.data.generated_pdf;
      
      setMessages(function(prev) { 
        return [...prev, { 
          role: 'assistant', 
          content: response.data.response,
          generatedImage: generatedImage,
          generatedPdf: generatedPdf
        }]; 
      });
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('فشل إرسال الرسالة');
      setMessages(function(prev) { return [...prev, { role: 'assistant', content: '**English:**\nSorry, an error occurred. Please try again.\n\n**العربية:**\nعذراً، حدث خطأ. يرجى المحاولة مرة أخرى.\n\n- Nicole, DHR' }]; });
    } finally {
      setLoading(false);
      clearAllFiles();
    }
  }

  function handleClear() {
    setMessages([]);
    setSessionId(null);
    clearAllFiles();
    toast.success('تم مسح المحادثة');
  }

  function handleQuickPrompt(prompt) {
    setInput(prompt);
  }

  const quickPrompts = [
    'حلل الملف المرفق',
    'لخص محتوى هذا الملف',
    'ما هي المواد المطلوبة؟',
    'أنشئ رد احترافي',
    'أنشئ Company Profile PDF',
    'أرسل PDF بالبريد',
    'أنشئ صورة لـ company profile',
    'اعرض الرسائل الجديدة',
  ];

  function getFileIcon(type) {
    if (!type) return '📎';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word')) return '📝';
    if (type.includes('excel') || type.includes('sheet')) return '📊';
    if (type.includes('image')) return '🖼️';
    return '📎';
  }

  return (
    <div className="space-y-4 h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <Bot className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">Nicole</h1>
            <p className="text-muted-foreground text-sm">مركز التحكم الذكي - Smart Control Center</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowControlPanel(!showControlPanel)}
            data-testid="toggle-control-panel-btn"
          >
            {showControlPanel ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
            {showControlPanel ? 'إخفاء' : 'عرض'} لوحة التحكم
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear} data-testid="clear-chat-btn">
            <Trash2 className="h-4 w-4 mr-1" />محادثة جديدة
          </Button>
        </div>
      </div>

      {/* Control Panel - Collapsible */}
      {showControlPanel && (
        <div className="space-y-4 animate-fade-in">
          {/* Quick Actions Bar */}
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-sm">إجراءات سريعة - Quick Actions</span>
                </div>
                <Button variant="ghost" size="sm" onClick={loadStats} disabled={loadingStats}>
                  <RefreshCw className={`h-4 w-4 ${loadingStats ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {quickActions.map((action, idx) => (
                  <Button 
                    key={idx}
                    variant="outline" 
                    className="h-auto py-3 flex flex-col items-center gap-1 hover:bg-primary/10 hover:border-primary/30"
                    onClick={action.action}
                    data-testid={`quick-action-${idx}`}
                  >
                    <action.icon className="h-5 w-5 text-primary" />
                    <span className="text-xs font-medium">{action.label}</span>
                    <span className="text-[10px] text-muted-foreground">{action.labelEn}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {controlPanelItems.map((category, catIdx) => (
              <Card key={catIdx} className="bg-card/50 border-border/50">
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {category.category}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-1">
                  {category.items.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center justify-between p-2 rounded-md transition-all hover:scale-[1.02] ${item.color}`}
                      data-testid={`nav-btn-${item.path.replace('/', '')}`}
                    >
                      <div className="flex items-center gap-2">
                        <item.icon className="h-4 w-4" />
                        <div className="text-right">
                          <span className="text-xs font-medium block">{item.label}</span>
                          <span className="text-[10px] opacity-70">{item.labelEn}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {item.stat !== undefined && item.stat > 0 && (
                          <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                            {item.stat}
                          </Badge>
                        )}
                        <ArrowRight className="h-3 w-3 opacity-50" />
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Chat Card */}
      <Card className={`bg-card border-border flex-1 flex flex-col ${showControlPanel ? 'min-h-[400px] md:min-h-[450px]' : 'min-h-[500px] md:min-h-[600px]'}`}>
        <CardHeader className="pb-2 px-4 py-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="h-5 w-5 text-primary" />
            محادثة مع Nicole - Chat with Nicole
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
          <ScrollArea className="flex-1 p-4 md:p-6" ref={scrollRef}>
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-8 md:py-12">
                  <div className="w-20 h-20 rounded-full bg-primary/20 mx-auto mb-4 flex items-center justify-center">
                    <Bot className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl uppercase mb-3">مرحباً! أنا Nicole</h3>
                  <p className="text-muted-foreground text-base md:text-lg mb-4 max-w-lg mx-auto">مساعدتك الذكية للتحكم بكافة أقسام البرنامج</p>
                  <p className="text-muted-foreground text-sm md:text-base mb-6 max-w-md mx-auto">يمكنني مساعدتك في: تحليل الملفات، إرسال البريد، إنشاء الصور، والمزيد!</p>
                  <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4">
                    {quickPrompts.map(function(prompt, index) {
                      return (
                        <Button key={index} variant="outline" size="default" className="text-sm md:text-base px-4 py-2" onClick={function() { handleQuickPrompt(prompt); }} data-testid={'quick-prompt-' + index}>
                          {prompt}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                messages.map(function(msg, index) {
                  return (
                    <div key={index} className={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                      <div className={'flex gap-3 max-w-[90%] md:max-w-[80%] ' + (msg.role === 'user' ? 'flex-row-reverse' : '')}>
                        <div className={'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ' + (msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
                          {msg.role === 'user' ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
                        </div>
                        <div className={'p-4 rounded-lg ' + (msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 border border-border')}>
                          {msg.files && msg.files.length > 0 && (
                            <div className="mb-3 pb-3 border-b border-current/20 space-y-1">
                              {msg.files.map((file, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <span className="text-lg">{getFileIcon(file.type)}</span>
                                  <span className="text-sm font-medium">{file.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <pre className="whitespace-pre-wrap font-sans text-sm md:text-base leading-relaxed">{msg.content}</pre>
                          {msg.generatedImage && msg.generatedImage.image_base64 && (
                            <div className="mt-4 space-y-2">
                              <img 
                                src={'data:image/png;base64,' + msg.generatedImage.image_base64} 
                                alt={msg.generatedImage.prompt || 'Generated image'}
                                className="max-w-full rounded-lg border border-border shadow-lg"
                                data-testid="generated-image"
                              />
                              <div className="flex items-center justify-between text-sm text-muted-foreground">
                                <span>🎨 {msg.generatedImage.style || 'realistic'}</span>
                                <a 
                                  href={'data:image/png;base64,' + msg.generatedImage.image_base64} 
                                  download={msg.generatedImage.file_name || 'generated_image.png'}
                                  className="text-primary hover:underline"
                                >
                                  ⬇️ تحميل
                                </a>
                              </div>
                            </div>
                          )}
                          {msg.generatedPdf && msg.generatedPdf.file_name && (
                            <div className="mt-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20 space-y-3">
                              <div className="flex items-center gap-2 text-green-600">
                                <FileText className="h-5 w-5" />
                                <span className="font-semibold">PDF جاهز للتحميل!</span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                <p>📄 عدد الصفحات: {msg.generatedPdf.total_pages}</p>
                                <p>🖼️ الصور المستخدمة: {msg.generatedPdf.images_used}</p>
                              </div>
                              <a 
                                href={process.env.REACT_APP_BACKEND_URL + '/api/design/pdf/' + msg.generatedPdf.file_name + '/download'}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="download-pdf-link"
                              >
                                ⬇️ تحميل PDF
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Bot className="h-5 w-5" />
                    </div>
                    <div className="bg-muted/50 border border-border p-4 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm md:text-base text-muted-foreground">Nicole تحلل وتفكر...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="p-4 md:p-5 border-t border-border space-y-3">
            {attachedFiles.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    الملفات المرفقة ({attachedFiles.length}/5)
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearAllFiles} className="text-xs text-destructive hover:text-destructive">
                    <X className="h-3 w-3 mr-1" />حذف الكل
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {attachedFiles.map((file) => (
                    <div key={file.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
                      <FileText className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium truncate max-w-[150px]">{file.name}</span>
                      <span className="text-xs text-muted-foreground">{Math.round(file.size / 1024)} KB</span>
                      <Button variant="ghost" size="icon" onClick={() => removeAttachedFile(file.id)} className="h-6 w-6 hover:bg-destructive/10">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.zip,.rar"
                className="hidden"
                multiple
                data-testid="file-input"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={function() { fileInputRef.current.click(); }}
                disabled={uploadingFile || attachedFiles.length >= 5}
                className="flex-shrink-0 h-12 w-12"
                data-testid="attach-file-btn"
              >
                {uploadingFile ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
              </Button>
              <Input
                value={input}
                onChange={function(e) { setInput(e.target.value); }}
                placeholder={attachedFiles.length > 0 ? `اسأل عن ${attachedFiles.length} ملف مرفق...` : "اكتب رسالتك هنا... (مثال: أرسل بريد إلى...)"}
                className="flex-1 bg-input/50 h-12 text-base md:text-lg px-4"
                onKeyPress={function(e) { if (e.key === 'Enter' && !e.shiftKey) handleSend(); }}
                disabled={loading}
                data-testid="chat-input"
              />
              <Button onClick={handleSend} disabled={loading || (!input.trim() && attachedFiles.length === 0)} className="flex-shrink-0 h-12 px-6 text-base" data-testid="send-btn">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5 mr-2" />إرسال</>}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              📎 يمكنك إرفاق حتى 5 ملفات (PDF, Word, Excel, TXT, Images - حتى 10MB لكل ملف) | 📧 يمكنك طلب إرسال بريد مباشرة
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ChatPage;
